import './Ecommerce/Store/ModelViewer.ts'
import {
  ASSETS_TYPES,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset
} from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import style from './gallery.module.scss'
import React, { useState, useEffect, useMemo } from 'react'
import PopupImage from '@aytm/app/swidget/base/components/PopupImage.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getAssetType } from './utils/getAssetType.ts'
import { getBigAssetPath } from '@aytm/lib/common/assets.ts'

interface IGalleryProps {
  schema: IEcommerceAnswerSchema
  externalStyle?: string
  emulatedMobile: boolean
  iconsPosition?: string
  magnifierClass?: string
  onAssetModelOpen?: (assetActiveIndex: number, tab: number) => void
}

const Gallery = ({ schema, externalStyle, emulatedMobile, magnifierClass, onAssetModelOpen }: IGalleryProps) => {
  const { t } = useSurveyLocale()
  const noAltFallback = t('questions', 'img', 'noAlt')
  const delta = schema.view3d ? 2 : 1
  const [current, setCurrent] = useState(
    (() => {
      if (!schema.gallery) return schema.view3d?.default ? 1 : 0
      const hasDefaultProduct = schema.gallery.findIndex(answer => answer.product.default)
      if (hasDefaultProduct < 0) return schema.view3d?.default ? 1 : 0
      return hasDefaultProduct + delta
    })()
  )

  const videos = useMemo(
    () =>
      [...(schema.gallery || []), ...(schema.view3d ? [schema.view3d] : [])].filter(
        asset => ASSETS_TYPES.VIDEO === getAssetType(asset)
      ),
    [schema]
  )
  const images = useMemo(
    () =>
      [
        ...(schema.image ? [schema.image] : []),
        ...(schema.view3d ? [schema.view3d] : []),
        ...(schema.gallery || [])
      ].filter(asset => ASSETS_TYPES.VIDEO !== getAssetType(asset)),
    [schema]
  )

  useEffect(() => {
    if (schema.gallery)
      schema.gallery.forEach(image => {
        const img = new Image()
        img.src = getBigAssetPath(image)
      })
  }, [schema.gallery])

  return (
    <div className={`${style.gallery} ${externalStyle ? style[externalStyle] : ''}`}>
      {schema.gallery || schema.view3d ? (
        <div className={style.icons}>
          <div className={`${0 == current ? style.current : ''} ${style.icon}`} onClick={() => setCurrent(0)}>
            <img src={schema.image?.trimmed_path || ''} alt={schema.image?.alt_text || noAltFallback} />
          </div>
          {schema.view3d ? (
            <div
              className={`${1 == current ? style.current : ''} ${style.icon} ${style.ar}`}
              onClick={() => setCurrent(1)}
            >
              <i className="icon-aytmfd-qshelf-ar" />
            </div>
          ) : (
            ''
          )}
          {(schema.gallery || []).map((_image, i) => (
            <div
              className={`${i == current - delta ? style.current : ''} ${style.icon}`}
              onClick={() => setCurrent(i + delta)}
              key={i}
            >
              {ASSETS_TYPES.VIDEO === getAssetType(schema.gallery[i]) ? (
                <div className={style.videoItem}>
                  <div className={style.layout} />
                  <img
                    className={style.videoPreview}
                    src={(schema.gallery[i] as unknown as IEcommerceVideoAsset).video.thumbnail}
                    alt=""
                    role="presentation"
                  />
                  <button className={style.play}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5v14l11-7z" fill="#fff" />
                    </svg>
                  </button>
                </div>
              ) : (
                <img src={schema.gallery[i].trimmed_path} alt={schema.gallery[i].alt_text || noAltFallback} />
              )}
            </div>
          ))}
        </div>
      ) : (
        ''
      )}
      {schema.view3d && 1 == current ? (
        <>
          {emulatedMobile ? (
            <div className={style.emulatedMobile}>
              <i className="icon-aytmfd-qshelf-ar"></i>
              <span>Preview on mobile device to see 3D rendering</span>
            </div>
          ) : (
            <model-viewer src={schema.view3d.file_url} ar camera-controls touch-action="pan-y" />
          )}
        </>
      ) : (
        <div className={style.photo}>
          <div
            className={style.photoContainer}
            onClick={() =>
              onAssetModelOpen(
                current ? images.indexOf(schema.gallery[current - delta]) : images.indexOf(schema.image),
                1
              )
            }
          >
            {/*<img src={getMediumAssetPath(!current ? schema.image : schema.gallery[current - delta])} />*/}
            {!current ? (
              <PopupImage
                src={schema.image ? getBigAssetPath(schema.image) : ''}
                altText={schema.image?.alt_text}
                enableMagnify={true}
                emulatedMobile={false}
                magnifierClass={magnifierClass}
                volume={false}
              />
            ) : ASSETS_TYPES.VIDEO === getAssetType(schema.gallery[current - delta]) ? (
              <div
                className={style.video}
                onClick={event => {
                  event.stopPropagation()
                  onAssetModelOpen(videos.indexOf(schema.gallery[current - delta]), 0)
                }}
              >
                <img
                  src={(schema.gallery[current - delta] as unknown as IEcommerceVideoAsset).video.thumbnail}
                  alt=""
                  role="presentation"
                />
                <button className={style.play}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5v14l11-7z" fill="#fff" />
                  </svg>
                </button>
              </div>
            ) : (
              <PopupImage
                src={getBigAssetPath(schema.gallery[current - delta])}
                altText={schema.gallery[current - delta].alt_text}
                enableMagnify={true}
                emulatedMobile={false}
                magnifierClass={magnifierClass}
                volume={false}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Gallery
